import { useEffect, useState } from 'react';
import { NudosButton, NudosTextInput } from '../../../../../components/NudosComponents';
import {
  IorganizationBusinessData,
  IorganizationCommercialPreference,
  COMMERCIAL_PREFERENCE_TYPES_UUIDS,
  IEmailInvitation
} from '../../../../../types/bussines';

import PurchaseOrderSelection from './components/PurchaseOrderSelection/PurchaseOrderSelection';

import './OrgDataSection.scss';
import { sendEmailInvitation } from '../../../../../services/bussines';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../utils/displayNudosStandardNotifications';
import { AxiosError } from 'axios';
import useOnlyDevelopers from '../../../../../state/setOnlyDevelopers';
import urlParameter from '../../../../../utils/urlParameter';

const OrgDataSection = ({
  organizationData,
  organizationId,
  updateWithoutLoaders
}: {
  organizationData: IorganizationBusinessData;
  organizationId?: string;
  updateWithoutLoaders?: () => void;
}) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [resetValues, setResetValues] = useState<boolean>(false);
  const [emailInvitation, setEmailInvitation] = useState<string>();
  const [loaderInvitation, setLoaderInvitation] = useState<boolean>(false);
  const validEmail = emailRegex.test(emailInvitation || '');
  const { onlyDevelopers } = useOnlyDevelopers();
  const enableSupportActions = !!urlParameter('enableSupportActions');

  const showSendInvitation = onlyDevelopers && enableSupportActions;

  const [organizationCommercialPreferencesMap, setOrganizationCommercialPreferencesMap] =
    useState<Map<string, IorganizationCommercialPreference>>();

  const { REQUIRE_PURCHASE_ORDER_FOR_ECOMMERCE_SALE, REQUIRE_PURCHASE_ORDER_FOR_LOGISTIC_SERVICE } =
    COMMERCIAL_PREFERENCE_TYPES_UUIDS;

  const getOrganizationCommercialPreferencesMap = (organizationPreferences: IorganizationCommercialPreference[]) => {
    const newPreferencesMap = new Map<string, IorganizationCommercialPreference>();
    for (const commercialPreference of organizationPreferences) {
      newPreferencesMap.set(commercialPreference.commercialPreferenceTypeUuid, commercialPreference);
    }
    setOrganizationCommercialPreferencesMap(newPreferencesMap);
  };

  const handleSendInvitation = async () => {
    if (!emailInvitation) return;
    setLoaderInvitation(true);
    try {
      const body: IEmailInvitation = { email: emailInvitation, isInvite: true, organizationId: organizationData.id };
      await sendEmailInvitation(body);

      displaySuccessNotification({
        customJSXMessage: <>Has enviado una inviado una invitación al correo {emailInvitation}</>
      });
      setEmailInvitation(undefined);
      setResetValues(true);
    } catch (error) {
      const { response } = error as AxiosError;
      const emailAlreadyRegistered = response?.data?.message === 'emailAlreadyRegistered';
      displayErrorNotification({
        customJSXMessage: emailAlreadyRegistered ? <>Este correo ya esta registraddo como usuario</> : undefined
      });
    } finally {
      setLoaderInvitation(false);
    }
  };

  useEffect(() => {
    getOrganizationCommercialPreferencesMap(organizationData.organizationCommercialPreferences || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationData]);

  return (
    <div className="orgDataSection">
      <div className="orgDataTitle">Información de la empresa</div>
      <div className="dataContainer">
        <div className="organizationNameContainer">
          <NudosTextInput
            label="Nombre de la empresa"
            styleLabel="orgSectionLabel"
            placeholder=""
            defaultValue={organizationData.name || 'Información incompleta'}
            isDeactivated
            isFilled
          />
        </div>
        <div className="organizationBusinessNameContainer">
          <NudosTextInput
            label="Razón social"
            styleLabel="orgSectionLabel"
            placeholder=""
            defaultValue={organizationData.businessName || 'Información incompleta'}
            isDeactivated
            isFilled
          />
        </div>
        <div className="countryAndTaxNumberContainer">
          <div className="country">
            <div className="label disabled">País*</div>
            <div className="flagContainer">
              {organizationData?.country?.flag ? (
                <img src={organizationData?.country?.flag} alt="Bandera" />
              ) : (
                'No Info'
              )}
            </div>
          </div>
          <div className="taxNumber">
            <NudosTextInput
              defaultValue={organizationData?.document || 'Información incompleta'}
              styleLabel="orgSectionLabel"
              label="Número de identificación fiscal*"
              isFilled
              isDeactivated
              placeholder=""
            />
          </div>
        </div>
      </div>
      <div className="dataContainer extraContainer">
        <div className="organizationNameContainer minSize">
          <NudosTextInput
            label="Empleados creados"
            styleLabel="orgSectionLabel"
            defaultValue={organizationData?.employeesCreated ? 'Sí' : 'NO'}
            placeholder=""
            isFilled
            isDeactivated
          />
        </div>
        <div className="organizationBusinessNameContainer minSize">
          <NudosTextInput
            label="Herramientas cargadas"
            styleLabel="orgSectionLabel"
            defaultValue={organizationData?.loadedTools ? 'Sí' : 'NO'}
            isDeactivated
            placeholder=""
            isFilled
          />
        </div>
      </div>
      {showSendInvitation && (
        <div className="sendInvitationToEmailContainer">
          <div className="invitationTitle">Enviar invitación a un correo</div>
          <div className="containerItems">
            <NudosTextInput
              isFilled={!!emailInvitation}
              placeholder="juanita@gmail.co"
              handleChange={e => {
                setEmailInvitation(e);
              }}
              label="Email"
              componentSize="extraLarge"
              isDeactivated={loaderInvitation}
              resetValues={resetValues}
              setResetValues={setResetValues}
            />
            <NudosButton
              customClassName="mt-3"
              buttonText="Enviar"
              componentSize="small"
              isButtonLoading={loaderInvitation}
              isButtonDisabled={loaderInvitation || !validEmail}
              handleClick={handleSendInvitation}
            />
          </div>
        </div>
      )}

      <div className="purchaseOrderContainer">
        <PurchaseOrderSelection
          organizationId={organizationId}
          organizationCommercialPreferencesMap={organizationCommercialPreferencesMap}
          updateData={updateWithoutLoaders}
          typeOfPurchaseOrder="órdenes"
          uuid={REQUIRE_PURCHASE_ORDER_FOR_ECOMMERCE_SALE}
        />

        <PurchaseOrderSelection
          organizationId={organizationId}
          organizationCommercialPreferencesMap={organizationCommercialPreferencesMap}
          updateData={updateWithoutLoaders}
          typeOfPurchaseOrder="servicios"
          uuid={REQUIRE_PURCHASE_ORDER_FOR_LOGISTIC_SERVICE}
        />
      </div>
    </div>
  );
};

export default OrgDataSection;
